import JSZip from 'jszip';

// Minimal, dependency-light real .xlsx writer. Produces a genuine OOXML workbook
// (a zip of XML parts) so Excel opens it as a modern .xlsx without the
// "file format and extension don't match" warning that the old HTML-table .xls
// approach triggered. Values are written as inline strings — formatting/links are
// already baked into the text by the caller (cleanValueForExport).

const escapeXml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// 0 -> "A", 25 -> "Z", 26 -> "AA"
const columnName = (index) => {
  let name = '';
  let n = index + 1;
  while (n > 0) {
    name = String.fromCharCode(65 + ((n - 1) % 26)) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
};

// Parse a plain numeric value (optionally currency-prefixed / thousands-separated) so
// Excel stores it as a real number the user can calculate on. Values with %, magnitude
// suffixes (K/M/B) or other units stay as text — converting them would change meaning.
const asNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const cleaned = value.trim().replace(/^[$€£¥]\s?/, '').replace(/,/g, '');
  return /^-?\d+(\.\d+)?$/.test(cleaned) ? Number(cleaned) : null;
};

const buildCellXml = (value, ref) => {
  const num = asNumber(value);
  if (num !== null) {
    // Numeric cell (default type "n") — calculable in Excel.
    return `<c r="${ref}"><v>${num}</v></c>`;
  }
  return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(value ?? '')}</t></is></c>`;
};

const buildSheetXml = (aoa) => {
  const rows = aoa
    .map((row, rowIndex) => {
      const cells = row
        .map((value, colIndex) => buildCellXml(value, `${columnName(colIndex)}${rowIndex + 1}`))
        .join('');
      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    `<sheetData>${rows}</sheetData></worksheet>`;
};

// Excel sheet names: max 31 chars, none of \ / ? * [ ] :
const safeSheetName = (name, index) => {
  const cleaned = String(name || '').replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 31);
  return cleaned || `Sheet${index + 1}`;
};

// Assemble the OOXML package (zip of XML parts) for the given sheets.
const buildWorkbookZip = (sheets) => {
  const zip = new JSZip();

  const sheetOverrides = sheets
    .map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`)
    .join('');

  zip.file('[Content_Types].xml',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
    `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
    `<Default Extension="xml" ContentType="application/xml"/>` +
    `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
    sheetOverrides +
    `</Types>`);

  zip.file('_rels/.rels',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>` +
    `</Relationships>`);

  const sheetTags = sheets
    .map((sheet, i) => `<sheet name="${escapeXml(safeSheetName(sheet.name, i))}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
    .join('');

  zip.file('xl/workbook.xml',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
    `<sheets>${sheetTags}</sheets></workbook>`);

  const sheetRels = sheets
    .map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`)
    .join('');

  zip.file('xl/_rels/workbook.xml.rels',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    sheetRels +
    `</Relationships>`);

  sheets.forEach((sheet, i) => zip.file(`xl/worksheets/sheet${i + 1}.xml`, buildSheetXml(sheet.aoa)));
  return zip;
};

/** Real .xlsx as a base64 string. Exposed for tests / non-DOM callers. */
export const buildXlsxBase64 = (sheets) => buildWorkbookZip(sheets).generate({ type: 'base64', compression: 'DEFLATE' });

/**
 * Build a real .xlsx from one or more sheets and trigger a download.
 * @param {string} filename Base name without extension.
 * @param {{ name: string, aoa: (string|number|null)[][] }[]} sheets Sheets to write (first row of each aoa = headers).
 */
export const downloadXlsx = (filename, sheets) => {
  const blob = buildWorkbookZip(sheets).generate({ type: 'blob', compression: 'DEFLATE' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.xlsx`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default downloadXlsx;
